import type { Bbox, LocationNode, ResolvedLocation } from '@/types/domain';
import { api } from './apiClient';

interface NodeRow {
  id: number;
  name: string;
  center: [number, number];
  bbox: Bbox;
}

/**
 * The il / ilçe / mahalle lists, served from the database rather than bundled.
 * The mahalle level is far too large to ship with the app, so all three levels
 * come from the same place to keep one source of truth for the names.
 *
 * Each level is fetched once per selection and never changes within a session,
 * so a successful result is memoised per key for the rest of the session. A
 * failed request is not memoised: its key is evicted as soon as it rejects, so
 * the next call retries instead of replaying the same failure forever. The
 * rejection itself is still propagated to the caller that triggered it —
 * eviction only clears the cache, it never hides the error.
 */
const cache = new Map<string, Promise<LocationNode[]>>();

const fetchLevel = (key: string, path: string): Promise<LocationNode[]> => {
  let pending = cache.get(key);
  if (!pending) {
    pending = api<NodeRow[]>(path).then((rows) =>
      rows.map((r) => ({ id: Number(r.id), name: r.name, center: r.center, bbox: r.bbox }))
    );
    cache.set(key, pending);
    // Only evict if this same promise is still the one cached — a retry that
    // was already started (and cached) while this one was failing must not be
    // thrown away by this handler running after the fact.
    pending.catch(() => {
      if (cache.get(key) === pending) cache.delete(key);
    });
  }
  return pending;
};

export const getProvinces = (): Promise<LocationNode[]> =>
  fetchLevel('provinces', '/locations/getProvinces');

export const getDistricts = (countyCode: number): Promise<LocationNode[]> =>
  fetchLevel(`districts:${countyCode}`, `/locations/getDistricts?county_code=${countyCode}`);

export const getNeighbourhoods = (townId: number): Promise<LocationNode[]> =>
  fetchLevel(`neighbourhoods:${townId}`, `/locations/getNeighbourhoods?town_id=${townId}`);

/** A level whose outline the map can draw. */
export type BoundaryLevel = 'province' | 'district' | 'neighbourhood';

/** Which level's outline to draw, and the id that addresses it at that level. */
export interface BoundaryTarget {
  level: BoundaryLevel;
  id: number;
}

/**
 * The deepest level of a selection that has an outline to draw, or undefined
 * when nothing is selected. One outline is drawn at a time: picking an ilçe
 * replaces the il's outline rather than nesting inside it, because two borders
 * on one map say less than one does.
 *
 * Each level is addressed by the id that level is keyed by, which is what the
 * cascade already reports - a plate code for a province, a row id below it.
 */
export function deepestBoundary(selection: {
  countyCode?: number;
  townId?: number;
  neighbourhoodId?: number;
}): BoundaryTarget | undefined {
  if (selection.neighbourhoodId != null) {
    return { level: 'neighbourhood', id: selection.neighbourhoodId };
  }
  if (selection.townId != null) return { level: 'district', id: selection.townId };
  if (selection.countyCode != null) return { level: 'province', id: selection.countyCode };
  return undefined;
}

// Outlines never change either, so they are memoised exactly like the level
// lists above, with the same eviction on failure.
const boundaries = new Map<string, Promise<object | null>>();

/**
 * One level's outline, as a GeoJSON geometry. Resolves to undefined for any
 * place that has no outline and for any failure to fetch one: the border is
 * orientation only, and a map without it still works.
 */
export function getBoundary(target?: BoundaryTarget): Promise<object | undefined> {
  if (!target) return Promise.resolve(undefined);
  const key = `${target.level}:${target.id}`;
  let pending = boundaries.get(key);
  if (!pending) {
    pending = api<object | null>(
      `/locations/getBoundary?level=${target.level}&id=${target.id}`
    );
    boundaries.set(key, pending);
    pending.catch(() => {
      if (boundaries.get(key) === pending) boundaries.delete(key);
    });
  }
  return pending.then((geometry) => geometry ?? undefined).catch(() => undefined);
}

/**
 * The address containing a point. Resolves to null when the point is outside
 * every mahalle — at sea, or across a border — in which case the caller leaves
 * the current selection untouched rather than showing an error.
 */
export async function resolveLocation(
  lat: number,
  lon: number
): Promise<ResolvedLocation | null> {
  const row = await api<{
    county_code: number;
    city: string;
    town_id: number;
    district: string;
    neighbourhood_id: number;
    neighbourhood: string;
  } | null>(`/locations/resolve?lat=${lat}&lon=${lon}`);
  if (!row) return null;
  return {
    countyCode: row.county_code,
    city: row.city,
    townId: row.town_id,
    district: row.district,
    neighbourhoodId: row.neighbourhood_id,
    neighbourhood: row.neighbourhood,
  };
}
